import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

/**
 * Handles email verification links (magic link, password reset, etc.)
 * by extracting the token_hash and type from query params and calling
 * Supabase's verifyOtp. This allows us to use branded fake-id.com URLs
 * instead of exposing the raw Supabase domain.
 */
const AuthVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token");
    const type = searchParams.get("type") as "magiclink" | "recovery" | "signup" | "invite" | "email_change" | "email";
    const redirectTo = searchParams.get("redirect_to") || "/account";

    if (!token || !type) {
      setError("Invalid verification link.");
      return;
    }

    const verify = async () => {
      try {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: type === "recovery" ? "recovery" : type === "magiclink" ? "magiclink" : "email",
        });

        if (verifyError) {
          console.error("Verification error:", verifyError);
          setError("This link has expired or has already been used. Please request a new one.");
          return;
        }

        // Redirect based on type
        if (type === "recovery") {
          navigate("/reset-password", { replace: true });
        } else {
          navigate(redirectTo, { replace: true });
        }
      } catch (err) {
        console.error("Unexpected error during verification:", err);
        setError("Something went wrong. Please try again.");
      }
    };

    verify();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-foreground mb-4">Verification Failed</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <a href="/account" className="inline-block bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold">
            Go to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Verifying your link...</p>
      </div>
    </div>
  );
};

export default AuthVerify;
